<template>
  <div
    v-if="showBanner"
    class="basic-ride-limit-value-upgrade-banner"
    :class="bannerToneClass"
  >
    <button
      type="button"
      class="basic-ride-limit-value-upgrade-banner__close"
      @click="isClosedManually = true"
    >
      <i class="fa fa-times" />
    </button>
    <span
      class="basic-ride-limit-value-upgrade-banner__icon"
      aria-hidden="true"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
        /><line
          x1="12"
          x2="12"
          y1="8"
          y2="12"
        /><line
          x1="12"
          x2="12.01"
          y1="16"
          y2="16"
        /></svg>
    </span>
    <div class="basic-ride-limit-value-upgrade-banner__body">
      <p class="basic-ride-limit-value-upgrade-banner__lead">
        Customer has
        <strong>{{ ridesRemaining }}</strong>
        rides available until
        <strong>{{ renewalDateDisplay }}</strong>
      </p>

      <p class="basic-ride-limit-value-upgrade-banner__label">
        Suggest upgrading to Value plan:
      </p>
      <p
        class="basic-ride-limit-value-upgrade-banner__script"
        :class="showRefuseUpgradeSection ? 'basic-ride-limit-value-upgrade-banner__script--spaced' : ''"
      >
        “{{ headsUpOpening }} {{ valueUpgradeContinuation }}”
      </p>

      <p-button
        v-if="canUpgrade"
        round
        type="primary"
        class="basic-ride-limit-value-upgrade-banner__upgrade-btn"
        @click="isUpgradeModalVisible = true">
        Upgrade to Value
      </p-button>

      <template v-if="showRefuseUpgradeSection">
        <p class="basic-ride-limit-value-upgrade-banner__label basic-ride-limit-value-upgrade-banner__label--section">
          If they refuse upgrading, make exception:
        </p>
        <p class="basic-ride-limit-value-upgrade-banner__script mb-0">
          “Ok no problem, I can make an exception for this ride.”
        </p>
      </template>
    </div>

    <BasicRideLimitValueUpgradeModal v-model:visible="isUpgradeModalVisible" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useProfile } from '@/use/profile';
import { planCurrency, planPricing, type PlanPricing } from '@/services/pricing';
import { planService } from '@/services/plan';
import { refundService } from '@/services/refund';
import type { CallerSubscriptionRefundData } from '@/models/refund';
import type { CustomizedPlan } from '@/models/plan';
import { PlanKind } from '@/gql/graphql';

import BasicRideLimitValueUpgradeModal from './BasicRideLimitValueUpgradeModal.vue';
import { isOperatorOrAbove } from '@/Utils/role.ts';

const RENEWAL_DATE_FMT = 'ddd, MMM D, YYYY';

const UPGRADE_NEXT_PLAN_PREFIXES = [
  PlanKind.Value,
  PlanKind.FixedIncome,
  PlanKind.BasicAnnual,
  PlanKind.Premium,
  PlanKind.TotalCare,
];

const { customerActivePlan, customerInfo } = useCustomer();
const { userRoles } = useProfile();

const canUpgrade = computed(() => userRoles.roles.some((role) => isOperatorOrAbove(role)));

const isClosedManually = ref(false);
const isUpgradeModalVisible = ref(false);
const refundData = ref<CallerSubscriptionRefundData | null>(null);
const valuePlan = ref<CustomizedPlan | null>(null);

const findPricing = (match: (pr: PlanPricing) => boolean) => planPricing.value.find(match) ?? null;

const basicPricing = computed(() =>
  findPricing((pr) => pr.monthly?.planKind === 'basic' || pr.annual?.planKind === 'basicAnnual'),
);

const hasRideLimitInPlanPricing = computed(() => {
  return planPricing.value.find(pr => pr[isAnnualPlan.value ? 'annual' : 'monthly']?.rideLimit);
});

const planName = computed(() => customerActivePlan.value?.planName ?? '');

const isAnnualPlan = computed(() => planName.value?.includes('Annual'));

const completedRides = computed(() => {
  const raw = customerActivePlan.value?.completedRidesCount;
  if (raw == null || Number.isNaN(Number(raw))) return 0;
  return Math.max(0, Math.floor(Number(raw)));
});

const ridesRemaining = computed(() => {
  const cap = isAnnualPlan.value ? basicPricing.value?.annual?.rideLimit : basicPricing.value?.monthly?.rideLimit;
  return cap == null ? 0 : Math.max(0, cap - completedRides.value);
});

const bannerToneClass = computed(() =>
  ridesRemaining.value === 0
    ? 'basic-ride-limit-value-upgrade-banner--danger'
    : 'basic-ride-limit-value-upgrade-banner--warning',
);

const renewalDateDisplay = computed(() => {
  const plan = customerActivePlan.value;
  if (!plan) return '';
  const end = plan.expectedPeriodEndAt ?? plan.periodEndAt;
  if (!end) return '';
  const tz = plan.timeZone ?? customerInfo.value?.timeZone ?? null;
  return util.formatDateTime(end, RENEWAL_DATE_FMT, tz) ?? '';
});

const cadenceNoun = computed(() => (isAnnualPlan.value ? 'year' : 'month'));

const upgradePlanPrice = computed(() => {
  const priceNumericCents = valuePlan.value?.priceNumericCents;
  return priceNumericCents == null ? null : priceNumericCents / 100;
});

const upgradePlanPriceDisplay = computed(() => {
  if (upgradePlanPrice.value == null || Number.isNaN(upgradePlanPrice.value)) return '—';
  return util.formatNullablePrice(upgradePlanPrice.value, planCurrency.value);
});

const proratedChargeToday = computed(() => {
  if (upgradePlanPrice.value == null || refundData.value == null) return null;
  return Math.max(0, Math.round((upgradePlanPrice.value - Number(refundData.value.refundAmount)) * 100) / 100);
});

const proratedChargeTodayDisplay = computed(() => util.formatNullablePrice(proratedChargeToday.value, planCurrency.value));

const headsUpOpening = computed(() => {
  const n = ridesRemaining.value;
  if (n === 0) {
    return `Just a heads up, you're at your ride limit for the ${cadenceNoun.value} on your current plan.`;
  }
  if (n === 1) {
    return `Just a heads up, you have 1 ride left for the ${cadenceNoun.value} on your current plan.`;
  }
  return `Just a heads up, you have ${n} rides left for the ${cadenceNoun.value} on your current plan.`;
});

const valueUpgradeContinuation = computed(() => {
  const line1 = `I can upgrade you right now to the Value plan, which doesn't have a ${cadenceNoun.value}ly ride limit`;

  if (proratedChargeToday.value != null) {
    return line1 + ` - the prorated charge today would be ${proratedChargeTodayDisplay.value}, and then it's ${upgradePlanPriceDisplay.value} per ${cadenceNoun.value}`;
  }

  if (upgradePlanPrice.value != null) {
    return line1 + ` - it's ${upgradePlanPriceDisplay.value} per ${cadenceNoun.value}`;
  }

  return line1 + `.`;
});

const showRefuseUpgradeSection = computed(() => ridesRemaining.value === 0);

const hasScheduledUpgradePlan = computed(() => {
  const currentPlanName = customerActivePlan.value?.planName;
  const nextPlanName = customerActivePlan.value?.nextPlanName;
  if (!nextPlanName || nextPlanName === currentPlanName) {
    return false;
  }
  return UPGRADE_NEXT_PLAN_PREFIXES.some((prefix) => nextPlanName.startsWith(prefix));
});

const showBanner = computed(() => {
  if (isClosedManually.value || hasScheduledUpgradePlan.value) {
    return false;
  }

  const rideLimitThreshold = isAnnualPlan.value ? 12 : 1;

  return (
    ridesRemaining.value <= rideLimitThreshold &&
    hasRideLimitInPlanPricing.value &&
    !!customerActivePlan.value &&
    !!renewalDateDisplay.value
  );
});

watch(customerInfo, (newValue, oldValue) => {
  if (newValue.id !== oldValue.id) {
    isClosedManually.value = false;
    refundData.value = null;
    valuePlan.value = null;
  }
});

watch(
  [showBanner, canUpgrade, () => customerInfo.value?.id],
  async ([show, hasUpgradePermission, callerId]) => {
    if (!show || !hasUpgradePermission || !callerId || (refundData.value && valuePlan.value)) {
      return;
    }

    try {
      const [plansResponse, refundResponse] = await Promise.all([
        planService.getAvailablePlansForCaller({ callerId }),
        refundService.getActiveSubscriptionRefundData({ callerId, refundType: 'pro-rated' }),
      ]);

      const plans = plansResponse ?? [];
      const targetPlanKind = isAnnualPlan.value ? PlanKind.ValueAnnual : PlanKind.Value;
      valuePlan.value =
        plans.find((plan) => plan.kind === targetPlanKind && plan.isDefault) ??
        plans.find((plan) => plan.kind === targetPlanKind) ??
        null;
      refundData.value = refundResponse;
    } catch {
      refundData.value = null;
      valuePlan.value = null;
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.basic-ride-limit-value-upgrade-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 1px solid var(--brl-border);
  border-radius: 6px;
  background-color: var(--brl-bg);
  color: var(--brl-text);
  padding: 20px;
  font-size: 14px;
  line-height: 1.45;
  position: relative;

  &--danger {
    --brl-bg: #fff0f0;
    --brl-border: #f8d7da;
    --brl-text: #dc2626;
  }

  &--warning {
    --brl-bg: #fffbeb;
    --brl-border: #f0e0a8;
    --brl-text: #B45309;
  }

  &__icon {
    flex-shrink: 0;
    margin-top: 1px;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__lead {
    font-size: 18px;
    margin: 0 0 14px;
    font-weight: 700;
  }

  &__label {
    margin: 0 0 6px;
    font-weight: 600;
  }

  &__label--section {
    margin-top: 12px;
  }

  &__script {
    margin: 0 0 6px;
    padding-left: 10px;
    font-style: italic;
    font-weight: 400;
  }

  &__script--spaced {
    margin-bottom: 12px;
  }

  &__upgrade-btn {
    margin-top: 4px;
    margin-left: calc(50% - 90px);
  }

  &__close {
    position: absolute;
    right: 5px;
    top: 5px;
    border: 0;
    background: transparent;
    cursor: pointer;
    color: #dc2626;
  }
}
</style>
